import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Badge,
  Row,
  Col,
  Statistic,
  Tag,
  Descriptions,
  Alert,
  message,
} from "antd";
import {
  BuildOutlined,
  ShopOutlined,
  ClockCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  ArrowLeftOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import images from "../../../assets/images";
import { useParams, useNavigate } from "react-router-dom";
import { getPremiseById, addIndependentUnit } from "../../../service/api";
import { useLegalContractTypesStore } from "../../../store/store";
import { useAuth } from "../../../auth/AuthProvider";
import { S3TempUrl } from "../../../utils/s3Client";
import { XIcon } from "lucide-react";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";

const { Option } = Select;

interface Premises {
  id: string;
  buildingName: string;
  size: string;
  ownership: "Owned" | "Rented" | "Leased";
  address: string;
  facilitiesAvailable: string;
  contracts: Contract[];
  status: "Active" | "Inactive";
  lastUpdated: string;
  hasIndependentUnits: boolean;
  independentUnits: IndependentUnit[];
  staff: StaffMember[];
  // Backend fields mapping
  premisesName?: string;
  addressLine1?: string;
  townCity?: string;
  postcode?: string;
  responsiblePerson?: string;
  responsiblePersonPhoneNumber?: string;
  facilities?: string;
  floor?: number;
  basement?: boolean;
  isStatus?: boolean;
  isEnabled?: boolean;
  region?: string;
  createdDate?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  tenantId?: string;
  admin?: string;
  modules?: any[];
  premisesModules?: any[];
  additionalBuildings?: any[];
}

interface IndependentUnit {
  id: string;
  name: string;
  vendor: string;
  location: string;
  ownerName: string;
  ownerPhone: string;
  ownerEmail: string;
  status: "Active" | "Inactive";
  lastUpdated: string;
  contractType?: string;
  contractCertificate?: {
    name?: string;
    url?: string;
    uploadedAt?: string;
  };
  contractCertificateName?: string;
  contractCertificateUrl?: string;
  contractCertificateUploadedAt?: string;
  staff: StaffMember[];
}

interface StaffMember {
  id: string;
  name: string;
  designation: string;
  phoneNumber: string;
  email?: string;
  entity: string;
  trainings: Training[];
  lastUpdated: string;
}

interface Training {
  id: string;
  name: string;
  completionDate: string;
  expiryDate?: string;
  status: "Completed" | "In Progress" | "Expired";
  certificate?: string;
}

interface Contract {
  id: string;
  name: string;
  type: "Rental" | "Lease" | "Service";
  startDate: string;
  endDate: string;
  vendor: string;
  amount: number;
  status: "Active" | "Expired" | "Terminated";
  documents: any[];
}

const PremisesInformationView: React.FC = () => {
  const { premiseId } = useParams<{ premiseId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const {
    legalContractTypesData,
    fetchLegalContractTypes,
    loading: legalContractTypesLoading,
  } = useLegalContractTypesStore();
  const { getsignedurl } = S3TempUrl();

  const [selectedPremises, setSelectedPremises] = useState<Premises | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [isUnitModalVisible, setIsUnitModalVisible] = useState(false);
  const [unitForm] = Form.useForm();
  const [unitFile, setUnitFile] = useState<File | null>(null);
  const [unitFileError, setUnitFileError] = useState<string>("");
  const unitFileInputRef = useRef<HTMLInputElement | null>(null);
  const [viewingContractDocument, setViewingContractDocument] = useState<{
    url: string;
    name: string;
  }>({
    url: "",
    name: "",
  });

  const contractTypeNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (legalContractTypesData || []).forEach((type) => {
      const key = type.id || (type as any)._id || type.legalContractName;
      if (key) {
        map.set(key, type.legalContractName || key);
      }
    });
    return map;
  }, [legalContractTypesData]);

  const getContractTypeLabel = (contractTypeId?: string) => {
    if (!contractTypeId) return "N/A";
    return contractTypeNameMap.get(contractTypeId) || contractTypeId;
  };

  // Fetch premise data
  const fetchPremiseData = async () => {
    if (!premiseId) return;

    try {
      setLoading(true);
      const response = await getPremiseById(premiseId);
      const premiseData = response.data;

      if (!premiseData) {
        message.error("Premise not found");
        navigate("/facility-information");
        return;
      }

      // Transform backend data to match frontend interface
      const transformedPremise: Premises = {
        id: premiseData.id || premiseData._id,
        buildingName:
          premiseData.premisesName || premiseData.buildingName || "N/A",
        size: premiseData.size || "N/A",
        ownership: premiseData.ownership || "Owned",
        address: premiseData.addressLine1 || premiseData.address || "N/A",
        facilitiesAvailable:
          premiseData.facilities || premiseData.facilitiesAvailable || "N/A",
        status: premiseData.isStatus ? "Active" : "Inactive",
        lastUpdated:
          premiseData.lastUpdated ||
          premiseData.updatedAt ||
          new Date().toISOString(),
        hasIndependentUnits: premiseData.hasIndependentUnits || false,
        independentUnits: premiseData.independentUnits || [],
        staff: premiseData.staff || [],
        contracts: premiseData.contracts || [],
        // Keep backend fields
        premisesName: premiseData.premisesName,
        addressLine1: premiseData.addressLine1,
        townCity: premiseData.townCity,
        postcode: premiseData.postcode,
        responsiblePerson: premiseData.responsiblePerson,
        responsiblePersonPhoneNumber: premiseData.responsiblePersonPhoneNumber,
        facilities: premiseData.facilities,
        floor: premiseData.floor,
        basement: premiseData.basement,
        isStatus: premiseData.isStatus,
        isEnabled: premiseData.isEnabled,
        region: premiseData.region,
        createdDate: premiseData.createdDate,
        updatedAt: premiseData.updatedAt,
        createdBy: premiseData.createdBy,
        updatedBy: premiseData.updatedBy,
        tenantId: premiseData.tenantId,
        admin: premiseData.admin,
        modules: premiseData.modules,
        premisesModules: premiseData.premisesModules,
        additionalBuildings: premiseData.additionalBuildings,
      };

      setSelectedPremises(transformedPremise);
    } catch (error) {
      console.error("Error fetching premise:", error);
      message.error("Failed to fetch premise data");
      navigate("/facility-information");
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount - wait for authentication to be ready
  useEffect(() => {
    // Only proceed when authentication is fully loaded and user is authenticated
    if (!authLoading && isAuthenticated && premiseId) {
      fetchPremiseData();
    }
  }, [premiseId, authLoading, isAuthenticated]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchLegalContractTypes();
    }
  }, [authLoading, isAuthenticated, fetchLegalContractTypes]);

  // Unit modal functions
  const showUnitModal = async () => {
    // Check if user is authenticated before opening modal
    if (!isAuthenticated) {
      message.error("Please log in to add units");
      navigate("/login");
      return;
    }

    setIsUnitModalVisible(true);
    unitForm.resetFields();
    setUnitFile(null);
    setUnitFileError("");
    if (unitFileInputRef.current) {
      unitFileInputRef.current.value = "";
    }
  };

  const handleUnitModalCancel = () => {
    setIsUnitModalVisible(false);
    unitForm.resetFields();
    setUnitFile(null);
    setUnitFileError("");
    if (unitFileInputRef.current) {
      unitFileInputRef.current.value = "";
    }
  };

  const handleUnitFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file =
      event.target.files && event.target.files[0]
        ? event.target.files[0]
        : null;
    setUnitFile(file);
    setUnitFileError("");
  };

  const handleUnitFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file =
      event.dataTransfer.files && event.dataTransfer.files[0]
        ? event.dataTransfer.files[0]
        : null;
    if (file) {
      setUnitFile(file);
      setUnitFileError("");
    }
  };

  const handleUnitFileDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleUnitSubmit = async (values: any) => {
    try {
      setLoading(true);

      if (!premiseId) {
        message.error("Premise ID not found");
        return;
      }

      if (unitFile && unitFile.size > 5 * 1024 * 1024) {
        setUnitFileError("File size cannot be more than 5MB");
        return;
      }

      let payload: any = values;

      if (unitFile) {
        const formData = new FormData();
        Object.entries(values).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value as any);
          }
        });
        formData.append("contractCertificate", unitFile);
        payload = formData;
      }

      // Call the API to add the independent unit
      await addIndependentUnit(premiseId, payload);

      message.success("Unit added successfully");
      handleUnitModalCancel();
      setUnitFile(null);
      setUnitFileError("");
      if (unitFileInputRef.current) {
        unitFileInputRef.current.value = "";
      }

      // Refresh premise data to show new unit
      await fetchPremiseData();
    } catch (error: any) {
      console.error("Error adding unit:", error);
      const errorMessage =
        error.response?.data?.error || error.message || "Failed to add unit";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getOwnershipColor = (ownership: string) => {
    switch (ownership) {
      case "Owned":
        return "green";
      case "Rented":
        return "orange";
      case "Leased":
        return "blue";
      default:
        return "default";
    }
  };

  const getStatusColor = (status: string) => {
    return status === "Active" ? "green" : "red";
  };

  const getMimeTypeFromKey = (key: string) => {
    const lower = key.toLowerCase();
    if (lower.endsWith(".pdf")) return "application/pdf";
    if (lower.endsWith(".docx"))
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    if (lower.endsWith(".doc")) return "application/msword";
    if (lower.endsWith(".pptx"))
      return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
    if (lower.endsWith(".ppt")) return "application/vnd.ms-powerpoint";
    if (lower.endsWith(".txt")) return "text/plain";
    if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
    if (lower.endsWith(".png")) return "image/png";
    return "application/octet-stream";
  };

  const handleViewContractDocument = async (unit: IndependentUnit) => {
    const certificateUrl =
      unit.contractCertificate?.url || unit.contractCertificateUrl;

    if (!certificateUrl) {
      message.warning("No certificate document available.");
      return;
    }

    try {
      const parsed = new URL(certificateUrl);
      const key = decodeURIComponent(parsed.pathname.replace(/^\//, ""));
      const mimeType = getMimeTypeFromKey(key);
      const signedUrl = await getsignedurl(key, mimeType);
      const documentName =
        unit.contractCertificate?.name ||
        unit.contractCertificateName ||
        "Contract Document";
      setViewingContractDocument({
        url: signedUrl,
        name: documentName,
      });
    } catch (error: any) {
      console.error("Failed to generate signed URL:", error);
      message.error("Unable to open certificate document.");
    }
  };

  // Show loading while fetching premise data
  if (loading) {
    return (
      <div className="p-4 max-w-8xl mx-auto mt-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!selectedPremises) {
    return (
      <div className="p-4 max-w-8xl mx-auto mt-8">
        <Alert
          message="Premise Not Found"
          description="The premise you're looking for doesn't exist or has been removed."
          type="error"
          showIcon
          action={
            <Button
              size="small"
              type="primary"
              onClick={() => navigate("/facility-information")}
            >
              Back to Facilities
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <>
      <div className="px-2 py-4 max-w-8xl mx-auto mt-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <div>
            <h2 className="text-2xl font-bold mb-1">
              {selectedPremises
                ? `${selectedPremises.buildingName} - Details`
                : "Premises Information Overview"}
            </h2>
            <div className="text-gray-500 text-base">
              {selectedPremises
                ? `Detailed view of ${selectedPremises.buildingName} including independent units, staff, and contracts`
                : "Comprehensive view of all premises, buildings, independent units, and staff information"}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={showUnitModal}
              disabled={loading}
            >
              <img src={images.addPremiessIcon} width={25} alt="" />
              ADD UNIT
            </button>
            <button
              className="flex items-center px-3 py-2 bg-[#61748e] text-white rounded-full"
              onClick={() => navigate("/facility-information")}
            >
              <ArrowLeftOutlined />
              Back
            </button>
          </div>
        </div>

        {/* Statistics Dashboard */}
        <Row gutter={16} className="mb-6">
          <Col span={6}>
            <Card>
              <Statistic
                title="Building Size"
                value={selectedPremises?.size || "N/A"}
                prefix={<BuildOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Ownership"
                value={selectedPremises?.ownership || "N/A"}
                prefix={<BuildOutlined />}
                valueStyle={{
                  color:
                    selectedPremises?.ownership === "Owned"
                      ? "#3f8600"
                      : "#faad14",
                }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Independent Units"
                value={selectedPremises?.independentUnits.length || 0}
                prefix={<ShopOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Last Updated"
                value={
                  selectedPremises?.lastUpdated
                    ? dayjs(selectedPremises.lastUpdated).format("MMM DD, YYYY")
                    : "N/A"
                }
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
        </Row>

        {selectedPremises && (
          <div className="space-y-6">
            {/* Premises Overview */}
            <Card title="Premises Overview" size="small">
              <Descriptions column={2}>
                <Descriptions.Item label="Building Name">
                  {selectedPremises.buildingName}
                </Descriptions.Item>
                <Descriptions.Item label="Size(sq ft)">
                  {selectedPremises.size}
                </Descriptions.Item>
                <Descriptions.Item label="Ownership">
                  <Tag color={getOwnershipColor(selectedPremises.ownership)}>
                    {selectedPremises.ownership}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Badge
                    status={getStatusColor(selectedPremises.status) as any}
                    text={selectedPremises.status}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Address" span={2}>
                  <EnvironmentOutlined className="mr-1" />
                  {selectedPremises.address}
                </Descriptions.Item>
                <Descriptions.Item label="Facilities Available" span={2}>
                  {selectedPremises.facilitiesAvailable
                    .split(", ")
                    .map((facility, index) => (
                      <Tag key={index} className="mb-1">
                        {facility}
                      </Tag>
                    ))}
                </Descriptions.Item>
                {selectedPremises.responsiblePerson && (
                  <Descriptions.Item label="Responsible Person" span={2}>
                    <UserOutlined className="mr-1" />
                    {selectedPremises.responsiblePerson}
                    {selectedPremises.responsiblePersonPhoneNumber && (
                      <span className="ml-2 text-gray-500">
                        <PhoneOutlined className="mr-1" />
                        {selectedPremises.responsiblePersonPhoneNumber}
                      </span>
                    )}
                  </Descriptions.Item>
                )}
                {selectedPremises.floor && (
                  <Descriptions.Item label="Number of Floors">
                    {selectedPremises.floor}
                  </Descriptions.Item>
                )}
                {selectedPremises.basement !== undefined && (
                  <Descriptions.Item label="Has Basement">
                    {selectedPremises.basement ? "Yes" : "No"}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Independent Units */}
            {selectedPremises.hasIndependentUnits &&
              selectedPremises.independentUnits.length > 0 && (
                <Card title="Independent Units" size="small">
                  <Table
                    columns={[
                      {
                        title: "Unit Name",
                        dataIndex: "name",
                        key: "name",
                      },
                      {
                        title: "Vendor",
                        dataIndex: "vendor",
                        key: "vendor",
                      },
                      {
                        title: "Location",
                        dataIndex: "location",
                        key: "location",
                      },
                      {
                        title: "Owner",
                        key: "owner",
                        render: (text: string, record: IndependentUnit) => (
                          <div>
                            <div className="font-medium">
                              {record.ownerName}
                            </div>
                            <div className="text-sm text-gray-500">
                              <PhoneOutlined className="mr-1" />
                              {record.ownerPhone}
                            </div>
                            <div className="text-sm text-gray-500">
                              <MailOutlined className="mr-1" />
                              {record.ownerEmail}
                            </div>
                          </div>
                        ),
                      },
                      {
                        title: "Contract",
                        key: "contract",
                        render: (_: string, record: IndependentUnit) => {
                          const contractLabel = getContractTypeLabel(
                            record.contractType,
                          );
                          const hasCertificate = Boolean(
                            record.contractCertificate?.url ||
                            record.contractCertificateUrl,
                          );
                          return (
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {contractLabel}
                              </span>
                              {hasCertificate ? (
                                <button
                                  type="button"
                                  className="text-teal-600 text-xs mt-1 text-left"
                                  onClick={() =>
                                    handleViewContractDocument(record)
                                  }
                                >
                                  View Document
                                </button>
                              ) : (
                                <span className="text-xs text-gray-500 mt-1">
                                  No document
                                </span>
                              )}
                            </div>
                          );
                        },
                      },
                      {
                        title: "Status",
                        dataIndex: "status",
                        key: "status",
                        render: (status: string) => (
                          <Badge
                            status={status === "Active" ? "success" : "error"}
                            text={status}
                          />
                        ),
                      },
                    ]}
                    dataSource={selectedPremises.independentUnits}
                    rowKey="id"
                    pagination={false}
                    size="small"
                  />
                </Card>
              )}

            {/* Contracts */}
            {selectedPremises.ownership !== "Owned" &&
              selectedPremises.contracts &&
              selectedPremises.contracts.length > 0 && (
                <Card title="Contracts" size="small">
                  <Table
                    columns={[
                      {
                        title: "Contract Name",
                        dataIndex: "name",
                        key: "name",
                      },
                      {
                        title: "Type",
                        dataIndex: "type",
                        key: "type",
                        render: (type: string) => <Tag>{type}</Tag>,
                      },
                      {
                        title: "Vendor",
                        dataIndex: "vendor",
                        key: "vendor",
                      },
                      {
                        title: "Duration",
                        key: "duration",
                        render: (text: string, record: Contract) => (
                          <div>
                            <div className="text-sm">
                              {dayjs(record.startDate).format("MMM DD, YYYY")}
                            </div>
                            <div className="text-sm text-gray-500">
                              to {dayjs(record.endDate).format("MMM DD, YYYY")}
                            </div>
                          </div>
                        ),
                      },
                      {
                        title: "Amount",
                        dataIndex: "amount",
                        key: "amount",
                        render: (amount: number) =>
                          `$${amount.toLocaleString()}`,
                      },
                      {
                        title: "Status",
                        dataIndex: "status",
                        key: "status",
                        render: (status: string) => (
                          <Badge
                            status={
                              status === "Active"
                                ? "success"
                                : status === "Expired"
                                  ? "error"
                                  : "default"
                            }
                            text={status}
                          />
                        ),
                      },
                    ]}
                    dataSource={selectedPremises.contracts}
                    rowKey="id"
                    pagination={false}
                    size="small"
                  />
                </Card>
              )}

            {/* Additional Buildings */}
            {selectedPremises.additionalBuildings &&
              selectedPremises.additionalBuildings.length > 0 && (
                <Card title="Additional Buildings" size="small">
                  <Table
                    columns={[
                      {
                        title: "Building Name",
                        dataIndex: "name",
                        key: "name",
                      },
                      {
                        title: "Location",
                        dataIndex: "location",
                        key: "location",
                      },
                      {
                        title: "Arrangement Type",
                        dataIndex: "arrangementType",
                        key: "arrangementType",
                      },
                      {
                        title: "Facilities",
                        dataIndex: "facilities",
                        key: "facilities",
                      },
                    ]}
                    dataSource={selectedPremises.additionalBuildings}
                    rowKey="id"
                    pagination={false}
                    size="small"
                  />
                </Card>
              )}
          </div>
        )}

        {/* Add Unit Modal */}
        <Modal
          title="Add New Independent Unit"
          open={isUnitModalVisible}
          onCancel={handleUnitModalCancel}
          footer={null}
          width={600}
          className="rounded-lg overflow-hidden"
          maskClosable={false}
          closable={false}
          zIndex={1000001}
          styles={{
            header: {
              borderBottom: "2px solid #e5e7eb",
              borderRadius: "8px 8px 0 0",
            },
            body: {
              borderRadius: "0",
              padding: "0",
            },
          }}
        >
          <Form
            form={unitForm}
            layout="vertical"
            onFinish={handleUnitSubmit}
            initialValues={{
              status: "Active",
            }}
            encType="multipart/form-data"
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Unit Name"
                  rules={[
                    { required: true, message: "Please enter unit name" },
                  ]}
                >
                  <Input placeholder="Enter unit name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="location"
                  label="Location"
                  rules={[{ required: true, message: "Please enter location" }]}
                >
                  <Input placeholder="Enter location" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="vendor"
                  label="Vendor"
                  rules={[
                    { required: true, message: "Please enter vendor name" },
                  ]}
                >
                  <Input placeholder="Enter vendor name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="ownerName"
                  label="Owner Name"
                  rules={[
                    { required: true, message: "Please enter owner name" },
                  ]}
                >
                  <Input placeholder="Enter owner name" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="contractType"
                  label="Contract Type"
                  rules={[
                    {
                      required: true,
                      message: "Please select a contract type",
                    },
                  ]}
                >
                  <Select
                    placeholder={
                      legalContractTypesLoading
                        ? "Loading contract types..."
                        : "Select contract type"
                    }
                    loading={legalContractTypesLoading}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {legalContractTypesData?.map((contractType) => (
                      <Option key={contractType.id} value={contractType.id}>
                        {contractType.legalContractName}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Contract Certificate">
                  <input
                    ref={unitFileInputRef}
                    type="file"
                    id="unitContractCertificate"
                    className="sr-only"
                    accept=".docx,.pdf,.txt,.jpg,.png,.ppt,.pptx"
                    onChange={handleUnitFileChange}
                    aria-hidden="true"
                    tabIndex={-1}
                  />
                  <div
                    className="border-dashed border-2 border-gray-300 rounded-md p-4 text-center cursor-pointer"
                    onClick={() => unitFileInputRef.current?.click()}
                    onDragOver={handleUnitFileDragOver}
                    onDrop={handleUnitFileDrop}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        unitFileInputRef.current?.click();
                      }
                    }}
                  >
                    <span className="block text-sm text-gray-600">
                      {unitFile
                        ? unitFile.name
                        : "Drag & drop contract certificates here or click to browse"}
                    </span>
                    <span className="block text-xs text-gray-500 mt-1">
                      Supported formats: DOCX, PDF, TXT, JPG, PNG, PPT, PPTX
                      (Max 5MB)
                    </span>
                  </div>
                  {unitFileError && (
                    <p className="text-red-500 text-sm mt-2">{unitFileError}</p>
                  )}
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="ownerPhone"
                  label="Owner Phone"
                  rules={[
                    { required: true, message: "Please enter owner phone" },
                  ]}
                >
                  <Input placeholder="Enter owner phone" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="ownerEmail"
                  label="Owner Email"
                  rules={[
                    { required: true, message: "Please enter owner email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input placeholder="Enter owner email" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: "Please select status" }]}
                >
                  <Select placeholder="Select status">
                    <Option value="Active">Active</Option>
                    <Option value="Inactive">Inactive</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          {/* Custom Footer with Styled Buttons */}
          <div className="flex bg-[#61748E] px-3 py-2 justify-end gap-4 -mx-6 -mb-6">
            <button
              onClick={handleUnitModalCancel}
              className="px-4 py-2 bg-gray-400 text-white rounded-full hover:bg-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => unitForm.submit()}
              disabled={loading}
              className="px-4 py-2 bg-teal-500 text-white rounded-full hover:bg-teal-600 transition-colors disabled:opacity-50"
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </Modal>
      </div>
      {viewingContractDocument.url !== "" && (
        <div
          className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute top-2 right-[115px] z-10 cursor-pointer bg-red-500 text-white rounded-full p-2"
            onClick={() => setViewingContractDocument({ url: "", name: "" })}
            aria-label="Close contract document viewer"
          >
            <XIcon className="w-5 h-5" />
          </button>
          <div className="relative bg-white border py-9 w-[calc(100vw-200px)] h-screen overflow-auto box-border">
            <DocumentViewer
              file={viewingContractDocument.url}
              name={viewingContractDocument.name}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default PremisesInformationView;
